import { useMemo } from "react";
import { useEnduserStore } from "../store/hooks";
import { evaluateVisibility } from "../../shared/visibility/evaluator";
import { extractDataSources } from "../../shared/visibility/extractDataSources";
import { useVisibilityContext } from "./useVisibilityContext";
import type { Block, LeafBlock } from "../../shared/schema/blocks";
import type { DataSourceType } from "@tellescope/visibility-schema";

export interface UseBlockVisibilityResult {
  /** Whether the block should be visible */
  isVisible: boolean;
  /** Whether visibility data is still loading */
  isLoading: boolean;
}

/**
 * Hook to check if a block should be visible based on its visibility condition.
 *
 * Automatically detects which data sources are needed from the condition
 * and lazily fetches them. While loading, returns `isVisible: true` to
 * prevent layout shift.
 *
 * @param block - The block to check visibility for
 * @returns Object with isVisible and isLoading flags
 */
export function useBlockVisibility(block: Block | LeafBlock): UseBlockVisibilityResult {
  // Extract required data sources from this block's condition
  const requiredSources = useMemo(() => {
    if (!block.visibility?.condition) return new Set<DataSourceType>();
    return extractDataSources(block.visibility.condition);
  }, [block.visibility?.condition]);

  // Fetch required data (lazy - only fetches what's needed)
  const { context, isLoading } = useVisibilityContext({ requiredSources });

  const isVisible = useMemo(() => {
    // No visibility condition = always visible
    if (!block.visibility?.condition) return true;

    // While loading, hide block to avoid content disappearing after load
    // This provides better UX than showing content that may vanish
    if (!context) return false;

    return evaluateVisibility(block.visibility.condition, context);
  }, [block.visibility?.condition, context]);

  return { isVisible, isLoading };
}

/**
 * Non-hook version of visibility evaluation for use in filter callbacks.
 * Uses store.getState() to access enduser data synchronously.
 *
 * NOTE: This function only supports enduser-based conditions. For conditions
 * that require async data (formResponse, etc.), use useBlockVisibility hook.
 *
 * @param block - The block to check visibility for
 * @returns true if the block should be visible, false otherwise
 */
export function checkBlockVisibility(block: Block | LeafBlock): boolean {
  // No visibility condition = always visible
  if (!block.visibility?.condition) {
    return true;
  }

  const enduser = useEnduserStore.getState().currentEnduser;

  // No enduser = hide conditional blocks
  if (!enduser) {
    return false;
  }

  // Note: This only evaluates with enduser data (synchronous)
  // Conditions using formResponse source will fail gracefully
  return evaluateVisibility(block.visibility.condition, { enduser });
}
